/**
 * Cross-user “learning” for an assistant is not something the browser can do alone.
 *
 * - **Per-user only**: session/local storage or client logs do not share signal across users.
 * - **Across users**: you typically need durable storage (a database or data lake), a small API to
 *   ingest anonymized events or Q&A, and a process to improve the product: RAG over approved docs,
 *   human-reviewed FAQ updates, fine-tuning, or analytics on common questions.
 *
 * This module supports an **optional** privacy-conscious hook: if you deploy a backend that writes
 * to your DB, set `VITE_ASSISTANT_TELEMETRY_URL` to that endpoint. Payloads avoid raw chat text by default.
 */

const URL_KEY = 'VITE_ASSISTANT_TELEMETRY_URL' as const;

export type AssistantTelemetryPayload = {
    event: string;
    /** Heuristic dashboard target from commerce guide (financing, partnerSync, …). */
    guideSection?: string;
    /** Count of user messages sent in the drawer session. */
    userTurns?: number;
    /** Count of assistant replies in the session. */
    assistantTurns?: number;
    [key: string]: unknown;
};

/** Fire-and-forget POST; no-op if env URL is unset (full URL to your ingest API). */
export function postAssistantTelemetry(payload: AssistantTelemetryPayload): void {
    const url = import.meta.env?.[URL_KEY] as string | undefined;
    if (!url || typeof fetch !== 'function') return;
    try {
        void fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...payload, ts: Date.now(), app: 'oneDRAW' }),
            keepalive: true,
        }).catch(() => {});
    } catch {
        /* ignore */
    }
}
